import { z } from 'zod';

// Assegnazione operatore per un lotto in pezzature (5/10/20/50/100g + peso totale)
export const mpgAssignmentSchema = z.object({
  operatorName: z.string().min(1, 'Operatore richiesto').max(100),
  lotId: z.string().uuid('Lotto richiesto'),
  qty5g: z.coerce.number().int().nonnegative().default(0),
  qty10g: z.coerce.number().int().nonnegative().default(0),
  qty20g: z.coerce.number().int().nonnegative().default(0),
  qty50g: z.coerce.number().int().nonnegative().default(0),
  qty100g: z.coerce.number().int().nonnegative().default(0),
  weightG: z.coerce.number().nonnegative().default(0),
  notes: z.string().max(500).nullable().optional(),
});
export type MpgAssignment = z.infer<typeof mpgAssignmentSchema>;

// Create: workDate (default today) + optional notes + assignments
export const mpgCreateRequestSchema = z.object({
  workDate: z.coerce.date().optional(),
  notes: z.string().max(2000).nullable().optional(),
  assignments: z.array(mpgAssignmentSchema).default([]),
});
export type MpgCreateRequest = z.infer<typeof mpgCreateRequestSchema>;

// Update: solo notes + assignments (workDate non modificabile post-create)
export const mpgUpdateRequestSchema = z.object({
  notes: z.string().max(2000).nullable().optional(),
  assignments: z.array(mpgAssignmentSchema).optional(),
});
export type MpgUpdateRequest = z.infer<typeof mpgUpdateRequestSchema>;

// Close: trigger transition DRAFT -> CLOSED + auto-rollover
export const mpgCloseRequestSchema = z.object({}).strict();
export type MpgCloseRequest = z.infer<typeof mpgCloseRequestSchema>;

// View: movimento del giorno (derivato da StockMovement)
export const mpgMovementViewSchema = z.object({
  id: z.string(),
  lotId: z.string(),
  lotCode: z.string().nullable(),
  productId: z.string(),
  productCode: z.string().nullable(),
  productName: z.string().nullable(),
  qty: z.string(),
  direction: z.enum(['IN', 'OUT', 'ADJ']),
  sourceDocumentId: z.string().nullable(),
  sourceDocumentType: z.enum(['PSC', 'LNP', 'MPG', 'MSS', 'MSF', 'INV']).nullable(),
  sourceDocumentCode: z.string().nullable(),
  occurredAt: z.string(),
  reason: z.string().nullable(),
});
export type MpgMovementView = z.infer<typeof mpgMovementViewSchema>;

// View: residuo lotti
export const mpgResiduoViewSchema = z.object({
  lotId: z.string(),
  lotCode: z.string(),
  productId: z.string(),
  productCode: z.string(),
  productName: z.string(),
  residualQty: z.string(),
  status: z.enum(['ACTIVE', 'BLOCKED', 'EXHAUSTED', 'ARCHIVED']),
});
export type MpgResiduoView = z.infer<typeof mpgResiduoViewSchema>;

// MPG assignment serializzato (con lotCode/productCode hydration)
export const mpgAssignmentResponseSchema = mpgAssignmentSchema.extend({
  lotCode: z.string().nullable(),
  productCode: z.string().nullable(),
  productName: z.string().nullable(),
});
export type MpgAssignmentResponse = z.infer<typeof mpgAssignmentResponseSchema>;

const documentStatusSchema = z.enum(['DRAFT', 'CLOSED', 'SUPERSEDED']);

export const mpgResponseSchema = z.object({
  id: z.string(),
  type: z.literal('MPG'),
  code: z.string().nullable(),
  workDate: z.string(),
  periodId: z.string(),
  period: z.object({
    year: z.number().int(),
    month: z.number().int(),
    status: z.enum(['OPEN', 'CLOSED']),
  }),
  status: documentStatusSchema,
  locked: z.boolean(),
  notes: z.string().nullable(),
  createdById: z.string(),
  closedById: z.string().nullable(),
  createdAt: z.string(),
  closedAt: z.string().nullable(),
  updatedAt: z.string(),
  assignments: z.array(mpgAssignmentResponseSchema),
  movimenti: z.array(mpgMovementViewSchema),
  residuo: z.array(mpgResiduoViewSchema),
});
export type MpgResponse = z.infer<typeof mpgResponseSchema>;

export const mpgListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  status: documentStatusSchema.optional(),
  periodId: z.string().uuid().optional(),
  workDateFrom: z.coerce.date().optional(),
  workDateTo: z.coerce.date().optional(),
});
export type MpgListQuery = z.infer<typeof mpgListQuerySchema>;

export const mpgListItemSchema = z.object({
  id: z.string(),
  type: z.literal('MPG'),
  code: z.string().nullable(),
  workDate: z.string(),
  status: documentStatusSchema,
  closedAt: z.string().nullable(),
  movementsCount: z.number().int().nonnegative(),
  assignmentsCount: z.number().int().nonnegative(),
});
export type MpgListItem = z.infer<typeof mpgListItemSchema>;

export const mpgListResponseSchema = z.object({
  items: z.array(mpgListItemSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});
export type MpgListResponse = z.infer<typeof mpgListResponseSchema>;
